import { CommonModule } from '@angular/common';
import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NebularModule } from '../../../modules/nebular/nebular.module';
import { convertToEnglishDigits, priceFormat } from '../../../utils/string';
import { ErrorFieldComponent } from '../error-field/error-field.component';

type InputAttrs = Partial<HTMLInputElement> & {
  readonly?: boolean;
};

@Component({
  standalone: true,
  selector: 'ngx-input-field',
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.scss'],
  imports: [CommonModule, NebularModule, ErrorFieldComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true,
    },
  ],
})
export class InputFieldComponent implements ControlValueAccessor {
  @Input() inputAttrs?: InputAttrs;
  @Input() label?: string;
  @Input() type?: string;
  @Input() unit?: string;
  @Input() placeholder?: string = '';
  @Input() autocomplete?: string;
  @Input() formatReadonlyList?: boolean = false;
  @Input() allowNegative?: boolean = false;

  @Input() control: any;

  value = '';
  formattedAsReadonlyList = false;
  @Input() disabled?: boolean = false;

  get inputType(): string {
    if (this.type === 'price') return 'tel';
    if (this.type === 'date') return 'text';
    return this.type || 'text';
  }

  get displayUnit(): string {
    return this.unit || (this.type === 'price' ? 'تومان' : '');
  }

  get inputDir(): string | null {
    return this.type === 'date' ? 'ltr' : this.inputAttrs?.dir || null;
  }

  get textareaDir(): string | null {
    return this.formattedAsReadonlyList ? 'ltr' : this.inputAttrs?.dir || null;
  }

  get readonlyAttr(): boolean | undefined {
    return this.inputAttrs?.readonly || this.inputAttrs?.readOnly || undefined;
  }

  writeValue(value: any): void {
    if (this.type === 'price') {
      this.value = value ? this.format(value.toString()) : '';
    } else {
      this.value = this.formatReadonlyListValue(value ?? '');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: any) {
    const input = event.target as HTMLInputElement;
    let raw = input.value;

    if (this.type === 'price') {
      const normalizedValue = convertToEnglishDigits(raw);
      const isNegative =
        this.allowNegative && /^\s*[-−]/.test(normalizedValue);
      const digits = normalizedValue.replace(/[^\d]/g, '');
      raw = `${isNegative ? '-' : ''}${digits}`;
      this.value = this.format(raw);
      this.onChange(raw); // 🔴 SEND RAW VALUE
    } else {
      this.value = raw;
      this.onChange(raw);
    }
  }

  private format(value: string): string {
    if (!value) return '';
    return priceFormat(value);
  }

  private formatReadonlyListValue(value: any): string {
    this.formattedAsReadonlyList = false;

    if (
      !this.formatReadonlyList ||
      this.type !== 'textarea' ||
      !this.readonlyAttr
    ) {
      return value;
    }

    const rawValue = String(value ?? '');
    const trimmed = rawValue.trim();
    if (!trimmed.includes('/') || /\s/.test(trimmed)) {
      return rawValue;
    }

    const parts = trimmed
      .split('/')
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length < 2 || parts.some((part) => !this.isNumeric(part))) {
      return rawValue;
    }

    this.formattedAsReadonlyList = true;

    if (this.isJalaliDateList(parts)) {
      return this.groupDateParts(parts).join('\n');
    }

    return parts.join('\n');
  }

  private isNumeric(value: string): boolean {
    return /^\d+$/.test(convertToEnglishDigits(value));
  }

  private isJalaliDateList(parts: string[]): boolean {
    if (parts.length < 6 || parts.length % 3 !== 0) {
      return false;
    }

    for (let i = 0; i < parts.length; i += 3) {
      const year = convertToEnglishDigits(parts[i]);
      const month = Number(convertToEnglishDigits(parts[i + 1]));
      const day = Number(convertToEnglishDigits(parts[i + 2]));

      if (
        !/^\d{4}$/.test(year) ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31
      ) {
        return false;
      }
    }

    return true;
  }

  private groupDateParts(parts: string[]): string[] {
    const dates: string[] = [];

    for (let i = 0; i < parts.length; i += 3) {
      dates.push(parts.slice(i, i + 3).join('/'));
    }

    return dates;
  }

  // Required for CVA
  onChange = (_: any) => {};
  onTouched = () => {};
}
